import { getOrganizationReference, joinReferenceString, joinTokenString } from "@/utils/medplum";

import {
  DOT_PHRASE_SCOPE_CODINGS,
  DOT_PHRASE_TYPE_CODING,
  DotPhraseScope,
} from "../constants/dot-phrases";

export interface DotPhraseQueryScope {
  scope: DotPhraseScope;
  practiceId: string;
  practitionerId: string;
}

/**
 * Read queries go to `DocumentReference` directly, never through the `List`. Permissions live on
 * the phrase and phrases are searchable on their own, so resolving `List` entries would add
 * nothing and would break server-side paging.
 *
 * Both scopes stay inside one practice through `custodian`, the practice the write path records as
 * owning the phrase. The access policies keep enforcing the organization compartment on every read,
 * so this narrows the result set and never widens it. The personal scope is narrowed further by
 * `author`, which is what makes a personal phrase private from everyone else.
 *
 * `status` is an argument because callers want different sets of it: a list read shows one status
 * at a time, while the trigger guard has to see every status that holds a trigger.
 */
export function buildScopeQuery(
  { scope, practiceId, practitionerId }: DotPhraseQueryScope,
  status: string,
) {
  const scopeCoding = DOT_PHRASE_SCOPE_CODINGS[scope];

  const base = {
    type: joinTokenString(DOT_PHRASE_TYPE_CODING.system, DOT_PHRASE_TYPE_CODING.code),
    category: joinTokenString(scopeCoding.system, scopeCoding.code),
    status,
    custodian: getOrganizationReference(practiceId),
  };

  if (scope === DotPhraseScope.Personal) {
    return { ...base, author: joinReferenceString("Practitioner", practitionerId) };
  }

  return base;
}
